import React, { useState, useEffect } from 'react';
import './StudentWallet.css';
import { Box, Typography, CircularProgress } from '@mui/material';
import KoinIcon from '../../../../../assets/Admin/EarningIcons/koin-icons.svg';
import ReceivedIcon from '../../../../../assets/Admin/EarningIcons/received-icon.svg';
import WithdrawalIcon from '../../../../../assets/Admin/EarningIcons/withdrawal-icon.svg';
import DisputeIcon from '../../../../../assets/Admin/EarningIcons/dispute-icon.svg';
import OneOnOneSessionIcon from '../../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import StudentWalletViewAll from './StudentWalletViewAll/StudentWalletViewAll';
import { axiosInstance } from '../../../../../utils/tokenUtils';

function formatText(input) {
  return input.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

export const EarningHistoryMap = {
  wallet_top_up: {
    class: 'received-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-earning-history-received-amount-text',
  },
  session_booking: {
    class: 'withdrawal-icon-container',
    icon: WithdrawalIcon,
    amountClass: 'single-earning-history-withdrawal-amount-text',
  },
  penalties: {
    class: 'dispute-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-earning-history-dispute-amount-text',
  },
};

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: { color: '#279BE2', bgcolor: '#FAFFFB', value: 'Group', icon: GroupSessionIcon },
  prime: { color: '#AD8B00', bgcolor: '#F0EFFF', value: 'Prime', icon: PrimeSessionIcon },
};

const StudentWallet = ({ studentId }) => {
  const [openViewAll, setOpenViewAll] = useState(false);
  const [walletDetails, setWalletDetails] = useState({
    total_balance: 0,
    total_spent: 0,
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [conversionRate, setConversionRate] = useState(7.5); // Default value

  useEffect(() => {
    const fetchWalletDetails = async () => {
      try {
        const response = await axiosInstance.get(`/admin/students/${studentId}/wallet_details`);
        setWalletDetails(response.data);
      } catch (error) {
        console.error('Error fetching wallet details:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchTransactionHistory = async () => {
      try {
        const response = await axiosInstance.get(
          `/admin/students/${studentId}/transactions-history?page=1&page_size=5`
        );
        setTransactionHistory(response.data.results || []);
      } catch (error) {
        console.error('Error fetching transaction history:', error);
      } finally {
        setHistoryLoading(false);
      }
    };

    const fetchConversionRate = async () => {
      try {
        const response = await axiosInstance.get('/auth/koins-to-riyal');
        setConversionRate(response.data.conversion_rate);
      } catch (error) {
        console.error('Error fetching conversion rate:', error);
        // Keep default value if API fails
      }
    };

    if (studentId) {
      fetchWalletDetails();
      fetchTransactionHistory();
    }
    fetchConversionRate();
  }, [studentId]);

  const handleViewAll = () => {
    setOpenViewAll(true);
  };

  const calculateKoins = usdAmount => {
    if (!usdAmount) return 0;

    // Handle both string and number inputs
    let numericAmount;
    if (typeof usdAmount === 'string') {
      numericAmount = parseFloat(usdAmount.replace(/[^0-9.-]+/g, ''));
    } else {
      numericAmount = parseFloat(usdAmount);
    }

    return isNaN(numericAmount) ? 0 : Math.round(numericAmount * conversionRate);
  };

  const formatDateTime = dateString => {
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getTransactionIcon = paymentType => {
    return EarningHistoryMap[paymentType] || EarningHistoryMap.session_booking;
  };

  const getAmountPrefix = paymentType => {
    return paymentType === 'wallet_top_up' ? '+' : '-';
  };

  return (
    <>
      {openViewAll ? (
        <StudentWalletViewAll studentId={studentId} />
      ) : (
        <Box className="flex flex-col gap-4 w-[100%]">
          {loading ? (
            <Box className="flex justify-center items-center py-8">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box className="flex flex-wrap gap-4 w-[100%]">
                <Box className="total-balance-container">
                  <div className="total-balance-text">Total Balance</div>
                  <Box className="flex gap-3 flex-col">
                    <div className="total-amount-value">
                      QR {calculateKoins(walletDetails?.total_balance || '0')}
                    </div>
                    <Box className="flex gap-2">
                      <img src={KoinIcon} alt="Koin" width="20px" height="20px" />
                      <div className="koin-amount text-white !important">
                        {walletDetails?.total_balance || '0'}
                      </div>
                    </Box>
                  </Box>
                  <Box className="koin-exchange-rate">1 Koin = {conversionRate} QR</Box>
                </Box>
                <Box className="total-spent-container">
                  <div className="locked-earning-text">Total Spent</div>
                  <Box className="flex gap-3 flex-col">
                    <div className="locked-amount-value">
                      QR {calculateKoins(walletDetails?.total_spent || '0')}
                    </div>
                    <Box className="flex gap-2">
                      <img src={KoinIcon} alt="Koin" width="20px" height="20px" />
                      <div className="koin-amount text-black !important">
                        {walletDetails?.total_spent || '0'}
                      </div>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className="flex justify-between items-center">
                <div className="history-text">Transaction History</div>
                <div className="view-all" onClick={handleViewAll}>
                  View All
                </div>
              </Box>
              <Box className="flex flex-col gap-4">
                {historyLoading ? (
                  <Box className="flex justify-center items-center py-4">
                    <CircularProgress size={24} />
                  </Box>
                ) : transactionHistory && transactionHistory.length > 0 ? (
                  transactionHistory.map(item => {
                    const transactionIcon = getTransactionIcon(item?.payment_type);
                    return (
                      <Box className="single-earning-history-container" key={item?.id}>
                        <Box className="flex items-center gap-4" style={{ width: '30%' }}>
                          <Box className={transactionIcon?.class}>
                            <img src={transactionIcon?.icon} alt={item?.payment_type} />
                          </Box>
                          <Box className="flex flex-col gap-1">
                            <div className="single-earning-history-title-text">
                              {formatText(item?.event_type_title)}
                            </div>
                            <div className="single-earning-history-title-text">
                              {formatText(item?.payment_type)}
                            </div>
                          </Box>
                        </Box>
                        <Box className="session-type-container">
                          {item?.session_type ? (
                            <>
                              <img
                                src={SessionTypeColor[item?.session_type]?.icon}
                                alt={item?.session_type}
                              />
                              <Typography
                                className="session-type-text"
                                sx={{ color: SessionTypeColor[item?.session_type]?.color }}
                              >
                                {SessionTypeColor[item?.session_type]?.value}
                              </Typography>
                            </>
                          ) : (
                            <Typography className="session-type-text" sx={{ color: '#949da6' }}>
                              ---
                            </Typography>
                          )}
                        </Box>
                        <div className="date-time-value" style={{ width: '20%' }}>
                          {formatDateTime(item?.created_at)}
                        </div>
                        <div
                          className={transactionIcon?.amountClass}
                          style={{ width: '10%', textAlign: 'right' }}
                        >
                          {getAmountPrefix(item?.payment_type)} {item?.koins} Koins
                        </div>
                      </Box>
                    );
                  })
                ) : (
                  <Box className="flex justify-center items-center py-4">
                    <Typography variant="body1" color="text.secondary">
                      No transaction history available
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default StudentWallet;
